import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebounce } from '@/hooks/use-debounce';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ScanLogsQuery,
  ScanLogsResponse,
  type ScanLogsResponse as ScanLogsResponseType,
} from '@tunodo/ticketing-shared';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const ticketingEventSchema = z.array(
  z.object({ id: z.string(), name: z.string(), date: z.string() }),
);

export const Route = createFileRoute('/_app/scan-logs')({
  validateSearch: ScanLogsQuery,
  component: ScanLogsPage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPageRange(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const delta = 2;
  const range: number[] = [];
  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    range.push(i);
  }

  const result: (number | 'ellipsis')[] = [1];
  if ((range[0] ?? 2) > 2) result.push('ellipsis');
  result.push(...range);
  if ((range[range.length - 1] ?? total - 1) < total - 1) result.push('ellipsis');
  result.push(total);
  return result;
}

function TypeBadge({ type }: { type: 'ATTENDANCE' | 'LUNCH' }) {
  if (type === 'ATTENDANCE') {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
        Entrada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800">
      Almuerzo
    </span>
  );
}

function ResultBadge({ result }: { result: 'OK' | 'DUPLICATE' | 'INVALID' | 'REVOKED' }) {
  if (result === 'OK') {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800">
        OK
      </span>
    );
  }
  if (result === 'DUPLICATE') {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800">
        Duplicado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800">
      {result === 'REVOKED' ? 'Revocado' : 'Inválido'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function ScanLogsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [emailInput, setEmailInput] = useState(search.email ?? '');
  const debouncedEmail = useDebounce(emailInput, 300);

  // Skip the first render so entering /scan-logs?email=foo doesn't reset page to 1
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    void navigate({
      search: (prev) => ({ ...prev, email: debouncedEmail.trim() || undefined, page: 1 }),
      replace: true,
    });
  }, [debouncedEmail, navigate]);

  const { data: ticketingEvents } = useQuery({
    queryKey: ['ticketing-events'],
    queryFn: () =>
      apiFetch('/api/passport-admin/ticketing-events', { schema: ticketingEventSchema }),
    staleTime: 60_000,
  });

  const showEventSelector = ticketingEvents && ticketingEvents.length > 1;

  const { data, isLoading } = useQuery<ScanLogsResponseType>({
    queryKey: [
      'scan-logs',
      {
        type: search.type,
        result: search.result,
        email: search.email,
        eventId: search.eventId,
        page: search.page,
        pageSize: search.pageSize,
      },
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search.type) params.set('type', search.type);
      if (search.result) params.set('result', search.result);
      if (search.email) params.set('email', search.email);
      if (search.eventId) params.set('eventId', search.eventId);
      params.set('page', String(search.page));
      params.set('pageSize', String(search.pageSize));
      return apiFetch(`/api/scans/logs?${params}`, { schema: ScanLogsResponse });
    },
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / search.pageSize)) : 1;

  return (
    <main className="flex-1 p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Logs de escaneos</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Historial completo de escaneos con filtros
        </p>
      </div>

      {/* Selector de evento — visible solo si hay múltiples eventos */}
      {showEventSelector && (
        <div className="max-w-xs">
          <Select
            value={search.eventId ?? 'all'}
            onValueChange={(value) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  eventId: value === 'all' ? undefined : value,
                  page: 1,
                }),
                replace: true,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos los eventos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los eventos</SelectItem>
              {ticketingEvents.map((ev) => (
                <SelectItem key={ev.id} value={ev.id}>
                  {ev.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
        <div className="flex-1 min-w-0">
          <Input
            placeholder="Buscar por email..."
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
          />
          {search.result === 'INVALID' && search.email && (
            <p className="text-xs text-muted-foreground mt-1">
              Los escaneos inválidos no tienen asistente asociado — el filtro por email no los
              incluirá.
            </p>
          )}
        </div>

        <Select
          value={search.type ?? 'all'}
          onValueChange={(value) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                type: value === 'all' ? undefined : (value as 'ATTENDANCE' | 'LUNCH'),
                page: 1,
              }),
              replace: true,
            })
          }
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="ATTENDANCE">Entrada (asistencia)</SelectItem>
            <SelectItem value="LUNCH">Almuerzo</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={search.result ?? 'all'}
          onValueChange={(value) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                result: value === 'all' ? undefined : (value as 'OK' | 'DUPLICATE' | 'INVALID'),
                page: 1,
              }),
              replace: true,
            })
          }
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Todos los resultados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los resultados</SelectItem>
            <SelectItem value="OK">OK</SelectItem>
            <SelectItem value="DUPLICATE">Duplicado</SelectItem>
            <SelectItem value="INVALID">Inválido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table className="min-w-[700px]">
          <TableHeader>
            <TableRow>
              <TableHead>Fecha/hora</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Resultado</TableHead>
              <TableHead>Asistente</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Escaneado por</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no identity
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-36" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-28" />
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                  No hay registros con los filtros seleccionados.
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              data?.items.map((scan) => (
                <TableRow key={scan.id}>
                  <TableCell className="text-sm whitespace-nowrap">
                    {new Date(scan.scannedAt).toLocaleString('es-CO')}
                  </TableCell>
                  <TableCell>
                    <TypeBadge type={scan.type} />
                  </TableCell>
                  <TableCell>
                    <ResultBadge result={scan.result} />
                  </TableCell>
                  <TableCell className="text-sm">{scan.attendee?.fullName ?? '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {scan.attendee?.email ?? scan.attemptedToken ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {scan.by.fullName}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
          <p className="text-sm text-muted-foreground order-2 sm:order-1">
            {data.total} registro{data.total !== 1 ? 's' : ''}
            {' · '}página {search.page} de {totalPages}
          </p>
          <Pagination className="w-auto order-1 sm:order-2">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  className={cn(
                    'cursor-pointer',
                    search.page <= 1 && 'pointer-events-none opacity-50',
                  )}
                  onClick={() =>
                    search.page > 1 &&
                    void navigate({
                      search: (prev) => ({ ...prev, page: search.page - 1 }),
                      replace: true,
                    })
                  }
                />
              </PaginationItem>

              {getPageRange(search.page, totalPages).map((item, i) =>
                item === 'ellipsis' ? (
                  // biome-ignore lint/suspicious/noArrayIndexKey: ellipsis separators have no identity
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={item}>
                    <PaginationLink
                      className="cursor-pointer"
                      isActive={item === search.page}
                      onClick={() =>
                        void navigate({
                          search: (prev) => ({ ...prev, page: item }),
                          replace: true,
                        })
                      }
                    >
                      {item}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}

              <PaginationItem>
                <PaginationNext
                  className={cn(
                    'cursor-pointer',
                    search.page >= totalPages && 'pointer-events-none opacity-50',
                  )}
                  onClick={() =>
                    search.page < totalPages &&
                    void navigate({
                      search: (prev) => ({ ...prev, page: search.page + 1 }),
                      replace: true,
                    })
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
      {data && data.total === 0 && null}
    </main>
  );
}
